import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '../ui/Icon';
import { useGlobalSearch, SearchResults } from '../../lib/api/search';
import { BrandAvatar } from '../../lib/brandAvatars';
import { Chip } from '../ui/Chip';
import { fmtRange } from '../../lib/dates';
import { assetDisplayTitle } from '../../lib/assetTitle';
import { TaskName } from '../tasks/TaskName';

/** Debounce a string value */
const useDebounced = <T,>(value: T, delayMs = 200): T => {
  const [v, setV] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setV(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);
  return v;
};

export const SearchBar = () => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [focused, setFocused] = useState(false);
  // Phones: the field is hidden behind a magnifier button.
  const [mobileOpen, setMobileOpen] = useState(false);
  const debounced = useDebounced(query, 200);
  const { data } = useGlobalSearch(debounced, focused);
  const rootRef = useRef<HTMLDivElement>(null);

  // Close the dropdown on outside click
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setFocused(false);
        setMobileOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  // Keyboard shortcut: / focuses search
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === '/' && e.target instanceof HTMLElement) {
        const tag = e.target.tagName;
        if (tag !== 'INPUT' && tag !== 'TEXTAREA' && !e.target.isContentEditable) {
          e.preventDefault();
          rootRef.current?.querySelector('input')?.focus();
        }
      }
      if (e.key === 'Escape' && focused) {
        setFocused(false);
        setMobileOpen(false);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [focused]);

  const goTo = (path: string) => {
    navigate(path);
    setQuery('');
    setFocused(false);
    setMobileOpen(false);
  };

  const hasResults =
    !!data &&
    data.brands.length + data.campaigns.length + data.assets.length + data.tasks.length > 0;
  const showDropdown = focused && debounced.trim().length >= 2;

  const goToAsset = (a: SearchResults['assets'][number]) => {
    if (a.campaign) goTo(`/campaigns/${a.campaign}`);
    else if (a.brands?.[0]) goTo(`/brands/${a.brands[0]}`);
    else goTo('/assets');
  };

  return (
    <div className={`search-wrap${mobileOpen ? ' open' : ''}`} ref={rootRef}>
      <button
        type="button"
        className="icon-btn search-toggle"
        aria-label="Search"
        onClick={() => {
          setMobileOpen(true);
          // Focus once the field is visible.
          setTimeout(() => rootRef.current?.querySelector('input')?.focus(), 0);
        }}
      >
        <Icon name="search" size={16} />
      </button>
      <div className="search">
        <Icon name="search" size={14} />
        <input
          type="text"
          placeholder="Search campaigns, brands, tasks…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setFocused(true)}
        />
        {query && (
          <button
            type="button"
            className="search-clear"
            aria-label="Clear search"
            onClick={() => {
              setQuery('');
              rootRef.current?.querySelector('input')?.focus();
            }}
          >
            <Icon name="x" size={12} />
          </button>
        )}
      </div>

      {showDropdown && (
        <div className="search-dropdown">
          {!data && (
            <div className="search-empty">Searching…</div>
          )}
          {data && !hasResults && (
            <div className="search-empty">No results for "{debounced}"</div>
          )}

          {data && data.brands.length > 0 && (
            <div className="search-section">
              <div className="search-section-title">
                Brands <span className="search-count">{data.brands.length}</span>
              </div>
              {data.brands.map((b) => (
                <button
                  key={b.id}
                  type="button"
                  className="search-row"
                  onClick={() => goTo(`/brands/${b.id}`)}
                >
                  <BrandAvatar brand={b} size={20} />
                  <div style={{ flex: 1, textAlign: 'left' }}>
                    <div className="search-row-title">{b.name}</div>
                    <div className="search-row-meta">{b.country || '—'}</div>
                  </div>
                </button>
              ))}
            </div>
          )}

          {data && data.campaigns.length > 0 && (
            <div className="search-section">
              <div className="search-section-title">
                Campaigns <span className="search-count">{data.campaigns.length}</span>
              </div>
              {data.campaigns.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  className="search-row"
                  onClick={() => goTo(`/campaigns/${c.id}`)}
                >
                  <div style={{ flex: 1, textAlign: 'left' }}>
                    <div className="search-row-title">{c.name}</div>
                    <div className="search-row-meta">
                      <span className="mono">
                        {c.start_date && c.end_date ? fmtRange(c.start_date, c.end_date) : 'To plan'}
                      </span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                    <Chip kind="type" value={c.type} />
                  </div>
                </button>
              ))}
            </div>
          )}

          {data && data.assets.length > 0 && (
            <div className="search-section">
              <div className="search-section-title">
                Placements <span className="search-count">{data.assets.length}</span>
              </div>
              {data.assets.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  className="search-row"
                  onClick={() => goToAsset(a)}
                >
                  <Icon name="image" size={14} />
                  <div style={{ flex: 1, textAlign: 'left' }}>
                    <div className="search-row-title">{assetDisplayTitle(a, { withCampaign: true })}</div>
                    <div className="search-row-meta">
                      <span>{a.asset_type}</span>
                      <span className="mono"> · {a.publish_date}</span>
                      {a.campaign_name ? <span> · {a.campaign_name}</span> : null}
                    </div>
                  </div>
                  <Chip kind="status" value={a.status} />
                </button>
              ))}
            </div>
          )}

          {data && data.tasks.length > 0 && (
            <div className="search-section">
              <div className="search-section-title">
                Tasks <span className="search-count">{data.tasks.length}</span>
              </div>
              {data.tasks.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  className="search-row"
                  onClick={() => goTo(`/campaigns/${t.campaign}`)}
                >
                  <Icon name="task" size={14} />
                  <div style={{ flex: 1, textAlign: 'left' }}>
                    <div className="search-row-title"><TaskName name={t.name} /></div>
                    <div className="search-row-meta mono">due {t.due_date}</div>
                  </div>
                  <Chip kind="status" value={t.status} />
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchBar;
